// ---------------------------------------------------------------------------
// Profiles Service - V11 Discovery API
// ---------------------------------------------------------------------------
// API client for V11 Discovery Profiles endpoint
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'
import type { DataSourceId } from '@/components/v11/discovery/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProfilesFilters {
  readonly page?: number
  readonly limit?: number
  readonly sort?: string
  readonly order?: 'asc' | 'desc'
  readonly search?: string
  readonly date_from?: string
  readonly date_to?: string
  readonly platform?: readonly string[]
  readonly gender?: readonly number[]
  readonly age?: readonly number[]
  readonly country?: readonly number[]
  readonly verified?: boolean
  readonly famous?: boolean
  readonly is_person?: boolean
  readonly processed?: boolean
  /** Include profiles that are the account's own channels (default: false) */
  readonly include_own_channels?: string
}

export interface ProfilesResponse {
  readonly success: boolean
  readonly data: readonly Record<string, unknown>[]
  readonly meta: {
    readonly page: number
    readonly limit: number
    readonly total: number
    readonly lastPage: number
    readonly hasMorePages: boolean
  }
}

export interface ProfileAggregation {
  readonly name: string
  readonly count: number
  readonly totalFollowers?: number
  readonly avgFollowers?: number
}

export interface ProfilesAggregationsResponse {
  readonly success: boolean
  readonly data: {
    readonly platforms: readonly ProfileAggregation[]
    readonly gender: readonly ProfileAggregation[]
    readonly age: readonly ProfileAggregation[]
    readonly countries: readonly ProfileAggregation[]
    readonly followerTiers: readonly ProfileAggregation[]
    readonly ethnicity: readonly ProfileAggregation[]
    readonly lifestyle: readonly ProfileAggregation[]
    readonly statusClass: readonly ProfileAggregation[]
    readonly industry: readonly ProfileAggregation[]
    readonly city: readonly ProfileAggregation[]
    readonly continent: readonly ProfileAggregation[]
    readonly verified: readonly ProfileAggregation[]
    readonly famous: readonly ProfileAggregation[]
    readonly isPerson: readonly ProfileAggregation[]
    readonly processed: readonly ProfileAggregation[]
    readonly brands: readonly ProfileAggregation[]
    readonly teams: readonly ProfileAggregation[]
    readonly sentiment: readonly ProfileAggregation[]
    readonly dominantEmotion: readonly ProfileAggregation[]
    readonly petFriendly: readonly ProfileAggregation[]
    readonly createdAt: readonly ProfileAggregation[]
    readonly authorCreatedAt: readonly ProfileAggregation[]
    readonly emotions: readonly ProfileAggregation[]
    readonly totals: {
      readonly profiles: number
      readonly followers: number
      readonly avgFollowers: number
      readonly avgSentiment: number
    }
  }
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const profilesService = {
  /**
   * Get profiles list with filters
   */
  async getProfiles(filters: ProfilesFilters = {}): Promise<ProfilesResponse> {
    const response = await api.get<ProfilesResponse>('/api/v11/discovery/profiles', {
      params: filters,
    })
    return response
  },

  /**
   * Get aggregated statistics for all profiles (used by charts)
   * Fast endpoint - returns pre-computed stats without loading individual profiles
   * Now supports filters! (Task #4)
   */
  async getAggregations(filters: ProfilesFilters = {}): Promise<ProfilesAggregationsResponse> {
    const response = await api.get<ProfilesAggregationsResponse>(
      '/api/v11/discovery/profiles/aggregations',
      { params: filters }
    )
    return response
  },

  /**
   * Get distribution for charts
   */
  async getDistribution(
    dimension: string,
    filters: ProfilesFilters = {}
  ): Promise<{ items: Array<{ label: string; value: number; percentage: number }>; total: number }> {
    const response = await api.get(`/api/v11/discovery/profiles/distribution`, {
      params: { dimension, ...filters },
    })
    return response.data
  },

  /**
   * Get trends data (Task #7 - implemented!)
   */
  async getTrends(
    metric: string,
    groupBy: string,
    filters: ProfilesFilters = {}
  ): Promise<{ success: boolean; data: { metric: string; groupBy: string; timeline: Array<Record<string, unknown>> } }> {
    const response = await api.get(`/api/v11/discovery/profiles/trends`, {
      params: { metric, group_by: groupBy, ...filters },
    })
    return response
  },
}
